<template>
  <div>
    <main class="container">
      <table-header
        title="Логи"
        :is-loading="isLoading"
        :is-empty="!logs.length"
      >
        <multiselect
          v-if="logTypes.length > 1"
          v-model="logTypeSelected"
          class="log-list-select"
          :options="logTypes"
          :allow-empty="false"
          :searchable="false"
          :close-on-select="true"
          :clear-on-select="false"
          placeholder=""
          deselect-label=""
          selected-label=""
          select-label=""
          label="label"
          @input="changeLogType"
        />
        <button
          class="btn btn-primary-ford-orange white"
          :disabled="isLoading || isProcessing"
          @click="exportXLSX"
        >
          <span
            v-if="isProcessing"
            class="spinner-border"
            style="width: 1.5rem; height: 1.5rem;"
            role="status"
            aria-hidden="true"
          ></span>
          <i v-if="!isProcessing" class="material-icons">file_download</i>
          Выгрузить в xls
        </button>
        <filter-logs :log-type="logTypeSelected" @applyFilters="applyFilters" />
      </table-header>
      <div class="table-wrapper">
        <div class="table-container">
          <transition name="fade" mode="out-in">
            <table v-if="!isLoading && logs.length" class="table">
              <thead>
                <tr>
                  <th
                    v-for="header in tableHeaders"
                    :key="header"
                    class="sortable sortable_asc"
                    :class="getSortClass(header)"
                    @click="changeSortKey(header)"
                  >
                    <div class="table__header">
                      {{ header }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="it in logs">
                  <tr :key="it.id">
                    <template v-for="header in tableHeaders">
                      <td v-if="header === 'date'" :key="header">
                        {{ it.date | parseDate }}
                      </td>
                      <td v-else-if="header === 'module'" :key="header">
                        {{ it.module ? getModuleDescription(it.module) : '' }}
                      </td>
                      <td v-else-if="header === 'user'" :key="header">
                        {{ it.user ? getUserDescription(it.user) : '' }}
                      </td>
                      <td v-else :key="header">
                        {{ it[header] }}
                      </td>
                    </template>
                  </tr>
                  <tr
                    v-if="!!it.data"
                    :key="'data' + it.id"
                    @click="showDetailInfoModal(it)"
                  >
                    <td class="detail-info-cell" :colspan="tableHeaders.length">
                      <small class="detail-info">{{
                        JSON.stringify(it.data)
                      }}</small>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </transition>
        </div>
      </div>
      <table-footer
        v-if="logs.length && !isLoading"
        :current-page="page"
        :per-page="pageSize"
        :limit="pageSize"
        :total-count="total"
        @pageChanged="v => (page = v)"
        @limitChanged="v => (pageSize = v)"
      />
    </main>
    <detail-info-modal
      v-model="isDetailInfoModalShown"
      :detail-info="detailInfo"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import TableHeader from '@/components/table/TableHeader';
import TableFooter from '@/components/table/TableFooter';
import DetailInfoModal from '@/components/common/DetailInfoModal';
import FilterLogs from '@/components/logs/FilterLogs';

const LogTypes = {
  Full: {
    name: 'full',
    label: 'Логи',
    headers: [
      'date',
      'severity',
      'name',
      'module',
      'service',
      'action',
      'requestID',
      'user',
    ],
  },
  ByUsers: {
    name: 'by-users',
    label: 'Отчёт по пользователям',
    headers: ['date', 'severity', 'name', 'user'],
  },
  ByCatalogs: {
    name: 'by-catalogs',
    label: 'Отчёт по каталогам',
    headers: ['date', 'severity', 'name', 'user'],
  },
  ByOrders: {
    name: 'by-orders',
    label: 'Отчёт по заказам',
    headers: ['date', 'severity', 'name', 'user'],
  },
  ByJobs: {
    name: 'by-jobs',
    label: 'Отчёт по рег.заданиям',
    headers: [
      'date',
      'severity',
      'name',
      'module',
      'service',
      'action',
      'requestID',
    ],
  },
  ByErrors: {
    name: 'by-errors',
    label: 'Отчёт по ошибкам',
    headers: ['date', 'severity', 'name', 'user'],
  },
};

export default {
  name: 'LogList',
  components: {
    TableHeader,
    TableFooter,
    DetailInfoModal,
    FilterLogs,
  },
  data: () => ({
    isLoading: true,
    isProcessing: false,
    logTypeSelected: null,
    logs: [],
    page: 1,
    pageSize: 10,
    total: 0,
    isFilterShown: false,
    filters: {},
    pageWatcherDisabled: false,
    sortReversed: true,
    sortKey: 'date',
    sort: '-date',
    isDetailInfoModalShown: false,
    detailInfo: null,
  }),
  computed: {
    ...mapState(['user']),
    tableHeaders() {
      if (this.logTypeSelected) {
        return this.logTypeSelected.headers;
      }
      return [];
    },
    logTypes() {
      const types = [];

      if (this.user.interfaces.includes('UM-logs')) {
        types.push(LogTypes.Full);
        types.push(LogTypes.ByJobs);
      }

      if (this.user.interfaces.includes('UM-SVO-reports')) {
        types.push(LogTypes.ByUsers);
        types.push(LogTypes.ByCatalogs);
        types.push(LogTypes.ByOrders);
        types.push(LogTypes.ByErrors);
      }

      return types;
    },
  },
  watch: {
    pageSize() {
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getLogs();
    },
    page() {
      if (this.pageWatcherDisabled) {
        return;
      }

      this.getLogs();
    },
    isDetailInfoModalShown(value) {
      this.$store.commit('setBackgroundBlur', value);
    },
  },
  created() {
    this.setDefaultLogType();
    this.setDefaultFilter();
    this.getLogs();
  },
  methods: {
    async exportXLSX() {
      this.isProcessing = true;

      try {
        const { name: type } = this.logTypeSelected;
        const fileContent = await this.$http.get('/event-log-report-xlsx', {
          responseType: 'blob',
          params: {
            type,
            sort: this.sort,
            ...(Object.keys(this.filters).length > 0
              ? { query: this.filters }
              : {}),
          },
        });

        const url = window.URL.createObjectURL(fileContent);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'event-log.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        throw err;
      } finally {
        this.isProcessing = false;
      }
    },
    async getLogs() {
      if (!this.logTypeSelected) {
        return;
      }

      this.isLoading = true;

      const { name: type, headers } = this.logTypeSelected;
      const { rows: logs, total } = await this.$http.get('/event-log-report', {
        params: {
          type,
          page: this.page,
          pageSize: this.pageSize,
          sort: this.sort,
          fields: [...headers, 'id', 'data'].join(' '),
          ...(Object.keys(this.filters).length > 0
            ? { query: this.filters }
            : {}),
        },
      });

      this.logs = logs;
      this.total = total;

      this.pageWatcherDisabled = false;
      this.isLoading = false;
    },
    applyFilters(filters) {
      this.filters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v != null && v !== ''),
      );
      this.pageWatcherDisabled = true;
      this.page = 1;
      this.getLogs();
    },
    changeSortKey(sortKey) {
      if (this.isLoading) {
        return;
      }

      this.sortReversed = this.sortKey === sortKey ? !this.sortReversed : false;
      this.sortKey = sortKey;

      this.sort = (this.sortReversed ? '-' : '') + this.sortKey;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getLogs();
    },
    getSortClass(sortKey) {
      return {
        [`sortable_active ${
          this.sortReversed ? 'sortable_desc' : 'sortable_asc'
        }`]: this.sortKey === sortKey,
      };
    },
    showDetailInfoModal(detailInfo) {
      this.isDetailInfoModalShown = true;
      this.detailInfo = detailInfo;
    },
    changeLogType() {
      this.pageWatcherDisabled = true;
      this.setDefaultFilter();
      this.page = 1;
      this.getLogs();
    },
    setDefaultFilter() {
      this.filters = {
        date: {
          $gte: this.$moment()
            .startOf('day')
            .toISOString(),
          $lte: this.$moment()
            .endOf('day')
            .toISOString(),
        },
      };
    },
    setDefaultLogType() {
      if (this.user.interfaces.includes('UM-logs')) {
        this.logTypeSelected = LogTypes.Full;
      } else if (this.user.interfaces.includes('UM-SVO-reports') || this.user.interfaces.includes('UM-SVO-readonly')) {
        this.logTypeSelected = LogTypes.ByUsers;
      }
    },
    getUserDescription(user) {
      let description = '';
      if (user.email) {
        description += user.email;
      }
      if (user.name) {
        description += description ? ', ' + user.name : user.name;
      }
      return description;
    },
    getModuleDescription(module) {
      let description = '';
      if (module.name) {
        description += module.name;
        if (module.version) {
          description += `@${module.version}`;
        }
      }

      return description;
    },
  },
};
</script>

<style lang="scss" scoped>
table th,
table td {
  max-width: 350px;
}

.detail-info-cell {
  max-width: auto;
  padding: 10px 40px 10px 30px !important;
  cursor: pointer;
  transition: background-color 0.1s ease-in;

  &:hover {
    background-color: rgba(69, 253, 161, 0.05);
  }
}

.detail-info {
  display: block;
  max-height: calc(20px + 1.5rem);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>

<style lang="scss">
.log-list-select {
  position: absolute !important;
  left: -110%;

  .multiselect__option--selected.multiselect__option--highlight {
    background: #f3f3f3;
    color: #35495e;
  }
}
</style>
